import { Flex, Box, Button, Progress } from '@mantine/core';
import { IconKey, IconRefresh, IconX } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import useLang from 'lang';
import SimpleLayout from 'layouts/SimpleLayout';
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RFID_PAGE_PATH } from 'routes/paths';
import theme from 'theme';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import P1Regular from 'theme/components/Typography/P1Regular';

import RfidPairingImg from 'assets/images/RFID-pairing.png';
import RfidSuccessImg from 'assets/images/RFID-succesful.png';
import RfidFailedImg from 'assets/images/RFID-failed.png';

type PairingStatus = 'pairing' | 'success' | 'failed';

const TIMEOUT_SECONDS = 30;

/**
 * Component for pairing a new RFID chip with a device.
 */
const RfidPair: React.FC = () => {
  const { _t } = useLang();
  const { getAction } = useApi();
  const navigate = useNavigate();
  const { deviceId } = useParams<{ deviceId: string }>();
  const [status, setStatus] = useState<PairingStatus>('pairing');
  const [timeLeft, setTimeLeft] = useState(TIMEOUT_SECONDS * 1000); // in milliseconds
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasErrorRef = useRef<boolean>(false);

  /**
   * Starts the RFID pairing process.
   */
  const startPairing = async () => {
    if (!deviceId) return;

    hasErrorRef.current = false;
    abortControllerRef.current = new AbortController();

    try {
      const pairRfid = getAction('PairRfid');
      const response = await pairRfid({
        payload: {
          deviceId: parseInt(deviceId, 10),
          timeout: TIMEOUT_SECONDS,
        },
      });

      if (response.success) {
        setStatus('success');
        if (intervalRef.current) clearInterval(intervalRef.current);
      } else {
        // API returned success=false, wait for countdown to finish
        hasErrorRef.current = true;
      }
    } catch (error) {
      if ((error as Error).name !== 'CanceledError') {
        // Store error but don't show failed yet - wait for countdown
        hasErrorRef.current = true;
      }
    }
  };

  useEffect(() => {
    startPairing();

    const UPDATE_INTERVAL = 50; // Update every 50ms for smooth animation
    intervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= UPDATE_INTERVAL) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          // Show failed only when countdown reaches 0
          setStatus('failed');
          return 0;
        }
        return prev - UPDATE_INTERVAL;
      });
    }, UPDATE_INTERVAL);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [deviceId]);

  /**
   * Handles cancel action and navigates back to RFID page.
   */
  const handleCancel = () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    navigate(RFID_PAGE_PATH.original);
  };

  /**
   * Handles done action and navigates back to RFID page.
   */
  const handleDone = () => {
    navigate(RFID_PAGE_PATH.original);
  };

  /**
   * Handles retry action and restarts the pairing process.
   */
  const handleRetry = () => {
    setStatus('pairing');
    setTimeLeft(TIMEOUT_SECONDS * 1000);
    hasErrorRef.current = false;

    // Restart countdown
    const UPDATE_INTERVAL = 50;
    intervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= UPDATE_INTERVAL) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          setStatus('failed');
          return 0;
        }
        return prev - UPDATE_INTERVAL;
      });
    }, UPDATE_INTERVAL);

    startPairing();
  };

  /**
   * Returns the appropriate image based on the current pairing status.
   */
  const getImage = () => {
    switch (status) {
      case 'success':
        return RfidSuccessImg;
      case 'failed':
        return RfidFailedImg;
      default:
        return RfidPairingImg;
    }
  };

  /**
   * Returns the appropriate title based on the current pairing status.
   */
  const getTitle = () => {
    switch (status) {
      case 'success':
        return _t('New chip has been successfully added. You can start using it.');
      case 'failed':
        return _t('Chip could not be recognized.');
      default:
        return _t('Please place the card or chip to the selected intercom.');
    }
  };

  /**
   * Returns the appropriate description based on the current pairing status.
   */
  const getDescription = () => {
    switch (status) {
      case 'success':
        return _t('You will be redirected to the chip list shortly.');
      case 'failed':
        return _t('Check if you are at the selected intercom and try again.');
      default:
        return null;
    }
  };

  const secondsLeft = Math.ceil(timeLeft / 1000);
  const progress = ((TIMEOUT_SECONDS * 1000 - timeLeft) / (TIMEOUT_SECONDS * 1000)) * 100;

  return (
    <SimpleLayout>
      <Flex direction="column" align="center" style={{ minHeight: '70vh' }} px="1rem" py="2.5rem" gap="1rem">
        <Box mb="1.5rem">
          <img src={getImage()} alt={_t('RFID')} style={{ maxWidth: '200px', height: 'auto' }} />
        </Box>
        <Box w="100%" maw="300px" style={{ textAlign: 'center' }}>
          <H2SemiBold color={theme.colors?.gray?.[12]}>{getTitle()}</H2SemiBold>
        </Box>

        {getDescription() && (
          <Box w="100%" maw="300px" style={{ textAlign: 'center' }}>
            <P1Regular color={theme.colors?.gray?.[10]}>{getDescription()}</P1Regular>
          </Box>
        )}

        {status === 'pairing' && (
          <>
            <P1Regular
              color={theme.colors?.gray?.[10]}
              style={{ paddingTop: '1rem', paddingBottom: '1rem', fontSize: '1.125rem' }}
            >
              {secondsLeft}s
            </P1Regular>
            <Box w="100%" maw="300px">
              <Progress value={progress} size="xl" radius="xl" color="gray.9" />
            </Box>
          </>
        )}
      </Flex>

      <Box style={{ position: 'fixed', bottom: '1.5rem', left: '1rem', right: '1rem' }} pb="0.5rem">
        {status === 'pairing' && (
          <Button variant="secondary" size="lg" leftSection={<IconX />} onClick={handleCancel} fullWidth>
            {_t('Cancel')}
          </Button>
        )}
        {status === 'success' && (
          <Button variant="secondary" size="lg" onClick={handleDone} fullWidth leftSection={<IconKey />}>
            {_t('Back to chip list')}
          </Button>
        )}
        {status === 'failed' && (
          <Flex gap="md" direction="column">
            <Button variant="primary" size="lg" onClick={handleRetry} leftSection={<IconRefresh />} fullWidth>
              {_t('Try again')}
            </Button>
            <Button variant="secondary" size="lg" onClick={handleCancel} leftSection={<IconX />} fullWidth>
              {_t('Cancel')}
            </Button>
          </Flex>
        )}
      </Box>
    </SimpleLayout>
  );
};

export default RfidPair;
